import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { SharedPetitionAnglesByTokenQuery, SharedPetitionAnglesByTokenQueryVariables } from './api.graphql';
import { SharedPetitionAnglesByToken } from './api.graphql';
import type { SharedPetitionAnglesBySlugQuery, SharedPetitionAnglesBySlugQueryVariables } from './apiBySlug.graphql';
import { SharedPetitionAnglesBySlug } from './apiBySlug.graphql';
import type { PetitionAnglesPageData } from './types';
import { PetitionAnglesPageDataError } from './types';

const TOKEN_RE = /^[0-9a-f]{64}$/;

export type GetPetitionAnglesPageDataOptions = {
	/**
	 * When `true`, throws `social_starter_not_found` if the record has no angles (today's
	 * social-starter-draft behavior). When `false`, a record with zero angles is returned
	 * successfully — callers that only need the starter's `username`/`sourceLocation`/`token`
	 * (e.g. the ambassador /start page) don't require any angles to exist.
	 */
	readonly requireAngles: boolean;
};

export async function getPetitionAnglesPageData(
	utilityContext: UtilityContext,
	identifier: string,
	options: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData> {
	if (TOKEN_RE.test(identifier)) {
		return getPetitionAnglesPageDataByToken(utilityContext, identifier, options);
	}
	return getPetitionAnglesPageDataBySlug(utilityContext, identifier, options);
}

async function getPetitionAnglesPageDataByToken(
	utilityContext: UtilityContext,
	token: string,
	{ requireAngles }: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData> {
	const { data } = await utilityContext.gql.fetch<
		SharedPetitionAnglesByTokenQuery,
		SharedPetitionAnglesByTokenQueryVariables
	>({
		query: SharedPetitionAnglesByToken,
		variables: { token },
		rejectOnError: true,
	});
	const response = data.socialStarterPetitionAnglesByToken;

	assertResponseUsable(response, requireAngles);

	return {
		token: response.token ?? token,
		...mapMetadata(response.metadata),
		drafts: response.angles ?? [],
		videoConsentCheckboxEnabled: data.featureConfigs.videoFirstPetitionPage,
	};
}

async function getPetitionAnglesPageDataBySlug(
	utilityContext: UtilityContext,
	slug: string,
	{ requireAngles }: GetPetitionAnglesPageDataOptions,
): Promise<PetitionAnglesPageData> {
	const { data } = await utilityContext.gql.fetch<
		SharedPetitionAnglesBySlugQuery,
		SharedPetitionAnglesBySlugQueryVariables
	>({
		query: SharedPetitionAnglesBySlug,
		variables: { slug },
		rejectOnError: true,
	});
	const response = data.socialStarterPetitionAnglesBySlug;

	assertResponseUsable(response, requireAngles);

	return {
		token: response.token ?? slug,
		...mapMetadata(response.metadata),
		drafts: response.angles ?? [],
		videoConsentCheckboxEnabled: data.featureConfigs.videoFirstPetitionPage,
	};
}

function assertResponseUsable(
	response: {
		readonly success: boolean;
		readonly angles?: readonly unknown[] | null;
	},
	requireAngles: boolean,
): void {
	if (!response.success) {
		throw new PetitionAnglesPageDataError('social_starter_not_found');
	}
	if (requireAngles && (!response.angles || response.angles.length === 0)) {
		throw new PetitionAnglesPageDataError('social_starter_not_found');
	}
}

function mapMetadata(
	metadata:
		| SharedPetitionAnglesByTokenQuery['socialStarterPetitionAnglesByToken']['metadata']
		| SharedPetitionAnglesBySlugQuery['socialStarterPetitionAnglesBySlug']['metadata'],
): Pick<
	PetitionAnglesPageData,
	'username' | 'name' | 'platform' | 'avatarUrl' | 'videoCoverUrl' | 'caption' | 'sourceLocation'
> {
	if (!metadata) {
		return {
			username: null,
			name: null,
			platform: null,
			avatarUrl: null,
			videoCoverUrl: null,
			caption: null,
			sourceLocation: null,
		};
	}
	const { username, name, platform, avatarUrl, videoCoverUrl, caption, sourceLocation } = metadata;
	return { username, name, platform, avatarUrl, videoCoverUrl, caption, sourceLocation: sourceLocation ?? null };
}
